import {faCheck, faTimes} from '@fortawesome/free-solid-svg-icons';

import './styles.css';

import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import BasicButton from 'components/BasicButton';
import FeaturesTable from 'components/FeaturesTable';
import FeaturesTableColumn from 'components/FeaturesTable/components/FeaturesTableColumn';
import FeaturesTableRow from 'components/FeaturesTable/components/FeaturesTableRow';
import LoadMask from 'components/LoadMask';
import PanelContainer from 'components/PanelContainer';
import Purchase from 'components/Purchase';
import {ANIMATION_TIMEOUT_APPEAR} from 'constants/animation.js';
import {ENDPOINT} from 'constants/endpoints';
import {ROUTE} from 'constants/route';

import {useApi} from 'hooks/useApi';

import PageLayout from 'layouts/PageLayout';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import MediaQuery from 'react-responsive';
import {CSSTransition} from 'react-transition-group';
import ResponseErrorDialog from 'scenes/Dialog/components/ResponseErrorDialog';
import LinkButton from '../../components/LinkButton';
import Segmented from '../../components/Segmented';

const PurchaseCandidate = props => {
  const { history } = props;
  const [selectedPriceType, setSelectedPriceType] = useState('');
  const [data, setData] = useState({});
  const [plans, setPlans] = useState([]);
  const [isFetching, setFetching] = useState(true);

  const features = useMemo(() => {
    return [...new Set(plans.map((plan) => {
      return plan.features;
    }).flat())];
  }, [plans]);

  const api = useApi();

  const handleStartPremium = useCallback((priceId) => {
    api.post(ENDPOINT.OTHER.PRICING_SUBSCRIBE, {priceId}, {
      handleForbidden: () => history.replace(ROUTE.ERROR_404),
      handleError: response => {
        let message = response.data.message;

        if (response.data.code === 'NO_PAYMENT_METHOD') {
          message = (
            <div>
              <p>{response.data.message}</p>
              <LinkButton text={'Add Payment Method'} link={'/candidate/account#billing'} />
            </div>
          )
        }

        ResponseErrorDialog({
          response: {
            data: {
              message,
            }
          }
        });
      },
      handleSuccess: response => {
        if (response.url) {
          window.location.href = response.url;
        }

        if (response.refresh) {
          window.location.reload();
        }
      },
    })
  }, []);

  useEffect(() => {
    api.get(
      ENDPOINT.OTHER.PRICING_PLANS,
      {},
      {
        handleNotFound: () => history.replace(ROUTE.ERROR_404),
        handleForbidden: () => history.replace(ROUTE.ERROR_404),
        handleError: response => ResponseErrorDialog({ response }),
        handleSuccess: response => {
          setData(response);
          setSelectedPriceType(response.priceTypes[0] || '');
        },
        handleComplete: () => setFetching(false),
      }
    );
  }, []);

  useEffect(() => {
    if (!data.plans || !selectedPriceType) return;

    const plans = data.plans.filter((plan) => {
      if (plan.id === 'free') {
        plan.price = {
          type: 'free',
          price: 'Free'
        };

        return true;
      }

      const pricing = plan.prices.find((pricing) => {
        return pricing.type === selectedPriceType;
      });

      plan.price = pricing;

      return !!pricing;
    });

    setPlans(plans);
  }, [data, selectedPriceType]);

  return (
    <PageLayout
      pageClass="purchase"
      pageTitle="Compare Plans"
      mainContent={
        <CSSTransition
          in={true}
          appear={true}
          timeout={ANIMATION_TIMEOUT_APPEAR}
          classNames="fade"
        >
          <PanelContainer
            styles={{
              marginBottom: '10px',
              paddingTop: '35px',
              position: 'relative',
            }}
          >
            <Purchase>
              {isFetching && <LoadMask relative/>}
              <MediaQuery query={'(min-width: 769px)'}>
                <div className="purchase__pricing">
                  <span className={'purchase__pricing-space'}></span>
                  <span className={'purchase__pricing-toggle'}>
                    <Segmented
                      checked={selectedPriceType}
                      options={(data.priceTypes || []).map(type => ({label: type, value: type}))}
                      onChange={value => {
                        setSelectedPriceType(value);
                      }}
                    />
                  </span>
                </div>
              </MediaQuery>
              <div className="purchase__header">
                <div className="purchase__block">
                  <h3 className="purchase__title">Compare Plans</h3>
                  <MediaQuery query={'(max-width: 768px)'}>
                    <div className="purchase__pricing purchase__pricing--mobile">
                      <span className={'purchase__pricing-toggle'}>
                        <Segmented
                          checked={selectedPriceType}
                          options={(data.priceTypes || []).map(type => ({label: type, value: type}))}
                          onChange={value => {
                            setSelectedPriceType(value);
                          }}
                        />
                      </span>
                    </div>
                  </MediaQuery>
                </div>

                {plans.map((plan) => {
                  let buttonText = 'Start Now';

                  if (data.currentPlan) {
                    buttonText = 'Change Plan';
                  }

                  if (plan.price.type === 'one-time') {
                    buttonText = 'Buy Now';
                  }

                  if (data.currentPlan === plan.price.id) {
                    buttonText = 'Current Plan';
                  }

                  return (
                    <div key={plan.id} className={classNames("purchase__item", {'purchase__item--accent': plan.highlighted})}>
                      <h4 className={classNames("purchase__heading", {'purchase__heading--accent': plan.highlighted})}>{plan.name}</h4>
                      <div className="purchase__price">
                        <div className="purchase__value">
                          {plan.price.type === 'free' ? plan.price.price : `$${plan.price.price}`}
                          {plan.price.type === 'one-time' || plan.price.type === 'free' ? null : (
                            <span className="purchase__accent purchase__accent--light">
                            /{plan.price.interval}
                          </span>
                          )}
                        </div>
                      </div>
                      {plan.price.type !== 'free' && (
                        <div className="purchase__action">
                          <BasicButton
                            disabled={data.currentPlan === plan.price.id}
                            btnWidth={140}
                            btnHeight={34}
                            btnFontWeight={600}
                            btnText={buttonText}
                            btnArrow={false}
                            btnBg="#fff"
                            btnHoverBg="#f40"
                            controlFunc={() => handleStartPremium(plan.price.id)}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="purchase__content">
                <FeaturesTable>
                  {features.map((feature, index) => {
                    const [title, description] = feature.split(':');
                    return (
                      <FeaturesTableRow key={index}>
                        <FeaturesTableColumn
                          title={title}
                          description={description}
                        />

                        {plans.map((plan, i) => {
                          const isAllowed = plan.features.includes(feature);

                          return (
                            <FeaturesTableColumn
                              key={`${index}-${i}`}
                              name={plan.name}
                              content={
                                <FontAwesomeIcon
                                  icon={isAllowed ? faCheck : faTimes}
                                  color={isAllowed ? '#27282d' : '#f40'}
                                  size="lg"
                                />
                              }
                            />
                          );
                        })}
                      </FeaturesTableRow>
                    );
                  })}
                </FeaturesTable>
              </div>
            </Purchase>
          </PanelContainer>
        </CSSTransition>
      }
    />
  );
};

export default PurchaseCandidate;
